import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import {
  CONTACT_METHODS,
  ENQUIRY_AREAS,
  ENQUIRY_CONSENT_WORDING,
} from '@/data/contact';
import { submitEnquiry } from '@/services/enquiryService';
import { trackEvent } from '@/lib/analytics';
import { Reveal } from '../Reveal';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  name: '',
  businessName: '',
  email: '',
  phone: '',
  location: '',
  supportRequired: '',
  message: '',
  preferredContactMethod: 'email',
  consent: false,
  website: '', // honeypot
};

const utmFromLocation = () => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return Object.keys(utm).length ? utm : undefined;
};

// Product deep-links (e.g. "Request Pricing" on /systems/compass or
// /systems/pulse) preselect the area of interest, prefill context and carry
// the real source page.
const PRODUCT_NAMES = { 'amden-compass': 'Amden Compass', 'amden-pulse': 'Amden Pulse', 'amden-foresee': 'Foresee', 'amden-reach': 'Amden Reach' };

const initialForm = () => {
  const next = { ...EMPTY };
  if (typeof window === 'undefined') return next;
  const params = new URLSearchParams(window.location.search);
  const product = PRODUCT_NAMES[params.get('interest')];
  if (product) {
    next.supportRequired = 'systems';
    if (params.get('topic') === 'pricing') {
      next.message = `I would like pricing information for ${product}.`;
    }
  }
  // Service detail deep-links (e.g. /growth/website-support "Request a Quote")
  const area = params.get('area');
  if (!product && ENQUIRY_AREAS.some((a) => a.value === area)) {
    next.supportRequired = area;
  }
  const service = params.get('service');
  if (service) {
    next.message = params.get('topic') === 'pricing'
      ? `I would like pricing information for ${service.slice(0, 80)}.`
      : `I would like to discuss ${service.slice(0, 80)}.`;
  }
  return next;
};

const sourceFromLocation = () => {
  if (typeof window === 'undefined') return 'Contact Page';
  const params = new URLSearchParams(window.location.search);
  return (params.get('source') || 'Contact Page').slice(0, 120);
};

const inputCls =
  'mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1';

const FieldError = ({ errors, id }) =>
  errors[id] ? (
    <p role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`error-${id}`}>
      {errors[id]}
    </p>
  ) : null;

// Section 3 — Contact enquiry form. Routes through the EXISTING secure
// /api/enquiries service (source "Contact Page") — no separate database,
// no duplicate service function. Success shows only after genuine storage.
export const ContactEnquiryForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');
  const [enquiryId, setEnquiryId] = useState(null);
  const startedRef = useRef(false);

  const set = (key) => (e) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent('enquiry_started');
    }
    const value = key === 'consent' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = 'Please enter your name.';
    if (form.businessName.trim().length < 2) er.businessName = 'Please enter your business name.';
    if (!EMAIL_RE.test(form.email.trim())) er.email = 'Please enter a valid email address.';
    if (!form.supportRequired) er.supportRequired = 'Please choose an area of interest.';
    if (form.message.trim().length < 10)
      er.message = 'Please tell us a little more (at least 10 characters).';
    if (!form.consent)
      er.consent = 'Please confirm you are happy for Amden Group to respond to your enquiry.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting' || status === 'success') return; // duplicate-submission guard
    setServerError('');
    if (!validate()) return;

    setStatus('submitting');
    try {
      const res = await submitEnquiry({
        name: form.name.trim(),
        businessName: form.businessName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        location: form.location.trim() || undefined,
        message: form.message.trim(),
        supportRequired: form.supportRequired,
        preferredContactMethod: form.preferredContactMethod,
        source: sourceFromLocation(),
        consent: form.consent,
        consentWording: ENQUIRY_CONSENT_WORDING,
        pageUrl: window.location.href,
        referrer: document.referrer || undefined,
        utm: utmFromLocation(),
        website: form.website,
      });
      setEnquiryId(res.enquiryId);
      setStatus('success'); // only reached after the backend stored the enquiry
      trackEvent('enquiry_submitted');
    } catch (err) {
      setStatus('error');
      setServerError(
        err?.response?.data?.detail
          ? 'Please check the highlighted fields and try again.'
          : 'Something went wrong while sending your enquiry. Please try again in a moment.'
      );
    }
  };

  return (
    <section id="contact-enquiry" aria-labelledby="enquiry-heading" data-testid="contact-enquiry" className="scroll-mt-28 bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">03</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Enquiry</span>
          </p>
          <h2 id="enquiry-heading" data-testid="enquiry-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Tell us how we can help
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Provide a little information about your business and the support you are looking for.
          </p>

          {status === 'success' ? (
            <div
              role="status"
              data-testid="enquiry-success"
              className="mt-10 flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-6"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" aria-hidden="true" />
              <div>
                <p className="text-base font-bold text-teal-900">
                  Thank you—your enquiry has been received. A member of the Amden team will be in
                  touch shortly.
                </p>
                {enquiryId && (
                  <p className="mt-1.5 text-sm text-teal-800">
                    Your enquiry reference is <span className="font-mono font-bold">{enquiryId}</span>.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="mt-10" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="text-sm font-bold text-navy-900">
                    Name <span className="text-gold-700" aria-hidden="true">*</span>
                  </label>
                  <input id="cf-name" type="text" autoComplete="name" value={form.name} onChange={set('name')} aria-invalid={!!errors.name} data-testid="cf-name" className={inputCls} />
                  <FieldError errors={errors} id="name" />
                </div>
                <div>
                  <label htmlFor="cf-business" className="text-sm font-bold text-navy-900">
                    Business name <span className="text-gold-700" aria-hidden="true">*</span>
                  </label>
                  <input id="cf-business" type="text" autoComplete="organization" value={form.businessName} onChange={set('businessName')} aria-invalid={!!errors.businessName} data-testid="cf-business-name" className={inputCls} />
                  <FieldError errors={errors} id="businessName" />
                </div>
                <div>
                  <label htmlFor="cf-email" className="text-sm font-bold text-navy-900">
                    Email address <span className="text-gold-700" aria-hidden="true">*</span>
                  </label>
                  <input id="cf-email" type="email" autoComplete="email" value={form.email} onChange={set('email')} aria-invalid={!!errors.email} data-testid="cf-email" className={inputCls} />
                  <FieldError errors={errors} id="email" />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="text-sm font-bold text-navy-900">
                    Phone number
                  </label>
                  <input id="cf-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} data-testid="cf-phone" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-location" className="text-sm font-bold text-navy-900">
                    Location
                  </label>
                  <input id="cf-location" type="text" placeholder="Town/County" value={form.location} onChange={set('location')} data-testid="cf-location" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-area" className="text-sm font-bold text-navy-900">
                    Area of interest <span className="text-gold-700" aria-hidden="true">*</span>
                  </label>
                  <select id="cf-area" value={form.supportRequired} onChange={set('supportRequired')} aria-invalid={!!errors.supportRequired} data-testid="cf-area" className={inputCls}>
                    <option value="">Select an area…</option>
                    {ENQUIRY_AREAS.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                  <FieldError errors={errors} id="supportRequired" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="text-sm font-bold text-navy-900">
                    How can we help? <span className="text-gold-700" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="cf-message"
                    rows={5}
                    value={form.message}
                    onChange={set('message')}
                    aria-invalid={!!errors.message}
                    data-testid="cf-message"
                    className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3.5 py-3 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                  />
                  <FieldError errors={errors} id="message" />
                </div>
              </div>

              <fieldset className="mt-6">
                <legend className="text-sm font-bold text-navy-900">Preferred contact method</legend>
                <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Preferred contact method">
                  {CONTACT_METHODS.map((m) => {
                    const active = form.preferredContactMethod === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm((f) => ({ ...f, preferredContactMethod: m.value }))}
                        data-testid={`cf-method-${m.value}`}
                        className={
                          active
                            ? 'rounded-full border-2 border-navy-900 bg-navy-900 px-4 py-2 text-sm font-bold text-white'
                            : 'rounded-full border-2 border-slate-200 bg-white px-4 py-2 text-sm font-bold text-navy-800 transition-colors hover:border-navy-300 hover:bg-navy-50'
                        }
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Honeypot — invisible to real users */}
              <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="cf-website">Website</label>
                <input id="cf-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
              </div>

              <div className="mt-6 flex items-start gap-3">
                <input
                  id="cf-consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={set('consent')}
                  aria-invalid={!!errors.consent}
                  data-testid="cf-consent"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                />
                <label htmlFor="cf-consent" className="text-sm leading-relaxed text-slate-600">
                  {ENQUIRY_CONSENT_WORDING}{' '}
                  <Link to="/privacy-policy" className="font-bold text-navy-900 underline-offset-2 hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>
              <FieldError errors={errors} id="consent" />

              {serverError && (
                <p role="alert" data-testid="cf-server-error" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-red-700">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  {serverError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                data-testid="cf-submit-btn"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {status === 'submitting' ? 'Sending…' : 'Send Enquiry'}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
